import {
	ChangeDetectionStrategy,
	Component,
	ViewChild,
	ViewEncapsulation,
	HostBinding,
	OnDestroy,
	ChangeDetectorRef,
} from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { Store } from "@ngrx/store";
import { CAFAuthenticatorService } from "@obg/mat.ui.common";
import { DialogService, NavbarOptions, DialogLayoutComponent } from "@obg/mat.ui.core";
import { CommonState } from "@obg/ngx.common";
import {
	AbstractTranslatableSnackBarService,
	Customer,
	CustomerManagementAction,
	ImageContent,
	MessageType,
} from "@obg/ngx.core";
import { CoreContentState, ImageSelector, TranslationGroup } from "@obg/ngx.core.content";
import { Subscription } from "rxjs";
import { switchMap, finalize, tap } from "rxjs/operators";

import { AnnualDataValidationEditContainer } from "../annual-data-validation-edit/annual-data-validation-edit.container";
import { ANNUAL_DATA_VALIDATION_EDIT_DIALOG_ID } from "../annual-data-validation.const";

@Component({
	selector: "obg-m-annual-data-validation-alert",
	templateUrl: "./annual-data-validation-alert.container.html",
	styleUrls: ["./annual-data-validation-alert.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class AnnualDataValidationAlertContainer implements OnDestroy {
	@HostBinding("class.obg-m-annual-data-validation-alert") hostCssClass = true;

	@ViewChild("dialog") dialogLayoutComponent!: DialogLayoutComponent;

	navbarOptions: NavbarOptions = {
		hideBackButton: true,
		showCloseButton: true,
		preventDefaultBack: false,
	};

	loadingSpinner = false;
	shouldShow = true;

	private data$$: Subscription = new Subscription();

	constructor(
		private matDialog: MatDialog,
		private dialogService: DialogService,
		private snackBarService: AbstractTranslatableSnackBarService,
		private imageSelector: ImageSelector,
		private store: Store<CommonState & CoreContentState>,
		private cafAuthenticatorService: CAFAuthenticatorService,
		private changeDetector: ChangeDetectorRef,
		private customerManagementAction: CustomerManagementAction
	) {}

	handleCustomerUpdateOutcome(isSuccessful: boolean) {
		this.matDialog.closeAll();
		this.store
			.select(
				this.imageSelector.getByKey("general", isSuccessful ? "general.success-icon" : "general.error-icon")
			)
			.pipe(
				switchMap((icon: ImageContent) =>
					this.snackBarService.openSnackBar$(
						{
							message: isSuccessful
								? "account-details.annual-data-validation.success-message"
								: "account-details.annual-data-validation.failure-message",
							dismissible: true,
							duration: 10000,
							icon,
							type: isSuccessful ? MessageType.Success : MessageType.Error,
						},
						[TranslationGroup.Common]
					)
				)
			)
			.subscribe(); // eslint-disable-line rxjs/no-ignored-subscription
	}

	openEditDialog() {
		this.shouldShow = false;
		const dialog = this.dialogService.openComponent(AnnualDataValidationEditContainer, {
			id: ANNUAL_DATA_VALIDATION_EDIT_DIALOG_ID,
			disableClose: true,
			closeOnNavigation: false,
		});

		this.data$$.add(
			dialog
				.afterClosed()
				.pipe(
					finalize(() => {
						this.shouldShow = true;
						this.changeDetector.markForCheck();
					})
				)
				.subscribe()
		);
	}

	openFacialRecognition() {
		this.shouldShow = false;
		this.data$$.add(
			this.cafAuthenticatorService
				.openCafAuthenticator$()
				.pipe(
					tap((auth) => {
						this.shouldShow = true;
						this.loadingSpinner = !!auth;
						this.changeDetector.markForCheck();
					})
				)
				.subscribe()
		);
	}

	updateCustomer(customer: Customer) {
		this.store.dispatch(this.customerManagementAction.getCustomerSuccess(customer));
	}

	ngOnDestroy(): void {
		this.data$$?.unsubscribe();
	}
}
